import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdPlatform, RewardAdFormat, RewardPerformanceResponse } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { Eye, MousePointerClick, Users, TrendingUp, Activity, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import AdDetailModal from './AdDetailModal';
import OpsSnapshot from './OpsSnapshot';

/** 전체 연계 행동 location (빈 데이터 포함 전부 표시) */
const ALL_ACTION_LOCATIONS: { watchLocation: string; watchLocationLabel: string }[] = [
  { watchLocation: 'ANALYSIS_UNLOCK', watchLocationLabel: '분석해제' },
  { watchLocation: 'DAILY_AD_BONUS', watchLocationLabel: '일일광고보너스' },
  { watchLocation: 'MILESTONE_AD_BONUS', watchLocationLabel: '마일스톤광고보너스' },
  { watchLocation: 'VOTE_AD_BONUS', watchLocationLabel: '투표광고보너스' },
  { watchLocation: 'CREATOR_MILESTONE_AD_BONUS', watchLocationLabel: '작성자마일스톤광고보너스' },
  { watchLocation: 'ANALYSIS_FREE_VIEW', watchLocationLabel: '무료분석조회' },
  { watchLocation: 'EXCHANGE', watchLocationLabel: '꺼내기' },
  { watchLocation: 'ATTENDANCE_AD_BONUS', watchLocationLabel: '출석광고보너스' },
  { watchLocation: 'ATTENDANCE_STREAK_SAVE', watchLocationLabel: '연속유지광고' },
  { watchLocation: 'COMMENT_AD_BONUS', watchLocationLabel: '댓글광고보너스' },
  { watchLocation: 'SURVEY_LIKE_AD_BONUS', watchLocationLabel: '설문좋아요광고보너스' },
  { watchLocation: 'COMMENT_LIKE_AD_BONUS', watchLocationLabel: '댓글좋아요광고보너스' },
  { watchLocation: 'WEEKLY_MISSION_AD_BONUS', watchLocationLabel: '이번주미션광고보너스' },
];

/** 플랫폼 필터 옵션 */
const PLATFORM_OPTIONS: { value: AdPlatform | undefined; label: string }[] = [
  { value: undefined, label: '전체' },
  { value: 'TOSS', label: 'TOSS' },
  { value: 'WEB', label: 'WEB' },
  { value: 'ANDROID', label: 'ANDROID' },
];

/** 기간 필터 옵션 */
const PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'YESTERDAY', label: '어제' },
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

const FORMAT_OPTIONS: { value: RewardAdFormat | undefined; label: string }[] = [
  { value: undefined, label: '전체' },
  { value: 'REWARDED', label: 'R' },
  { value: 'INTERSTITIAL', label: 'F' },
];

/**
 * 광고 성과 대시보드
 */
export const AdPerformanceDashboard = () => {
  const [platform, setPlatform] = useState<AdPlatform | undefined>(undefined);
  const [actionPeriod, setActionPeriod] = useState('7D');
  const [actionFormat, setActionFormat] = useState<RewardAdFormat | undefined>(undefined);
  const [rewardPeriod, setRewardPeriod] = useState('7D');
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);
  const [selectedAdTitle, setSelectedAdTitle] = useState<string>('');

  /** 통계 요약 */
  const { data: summary } = useQuery({
    queryKey: ['adStatsSummary', platform],
    queryFn: () => adApi.getStatsSummary(platform).then((res) => res.data),
  });

  /** 광고별 통계 목록 */
  const { data: statsList } = useQuery({
    queryKey: ['adStatsList', platform],
    queryFn: () => adApi.getStatsList(platform).then((res) => res.data),
  });

  /** 활성 광고 수 (목록에서 집계) */
  const { data: adList } = useQuery({
    queryKey: ['adminAds'],
    queryFn: () => adApi.getAdList().then((res) => res.data),
  });
  const activeCount = adList?.filter((ad) => ad.isActive).length ?? 0;

  /** 연계 행동 통계 */
  const { data: actionStats } = useQuery({
    queryKey: ['adActionStats', actionPeriod, platform, actionFormat],
    queryFn: () => adApi.getActionStats(actionPeriod, platform, actionFormat).then((res) => res.data),
  });

  /** 리워드 광고 성과 */
  const { data: rewardPerf } = useQuery({
    queryKey: ['rewardPerformance', rewardPeriod],
    queryFn: () => adApi.getRewardPerformance(rewardPeriod).then((res) => res.data as RewardPerformanceResponse),
  });

  /** 요약 데이터 */
  const totalPv = summary?.totalImpressions ?? 0;
  const totalClicks = summary?.totalClicks ?? 0;
  const ctr = summary?.overallCtr ?? 0;

  /** 차트 데이터 */
  const chartData = (statsList ?? []).map((item) => ({
    name: item.title.length > 8 ? item.title.slice(0, 8) + '…' : item.title,
    PV: item.pv,
    클릭: item.clicks,
  }));

  /** 연계 행동: API 응답을 전체 location 목록과 병합 (0건인 location도 표시) */
  const mergedActionStats = ALL_ACTION_LOCATIONS.map((loc) => {
    const count = (actionStats ?? [])
      .filter((item) => item.watchLocation === loc.watchLocation)
      .reduce((sum, item) => sum + item.count, 0);
    return {
      watchLocation: loc.watchLocation,
      watchLocationLabel: loc.watchLocationLabel,
      count,
    };
  });

  /** 연계 행동 합계 */
  const actionTotal = mergedActionStats.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-6">
      {/* G1-2: 운영 설정 스냅샷 (상단 배치) */}
      <OpsSnapshot />

      {/* 광고 상세 성과 모달 */}
      {selectedAdId !== null && (
        <AdDetailModal adId={selectedAdId} adTitle={selectedAdTitle} onClose={() => setSelectedAdId(null)} />
      )}

      {/* 플랫폼 필터 */}
      <div className="flex gap-2">
        {PLATFORM_OPTIONS.map((opt) => (
          <Button
            key={opt.label}
            variant={platform === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPlatform(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {/* ==================== 리워드 광고 성과 ==================== */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">리워드 광고 성과</h3>
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={rewardPeriod === opt.value ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7 px-2"
                onClick={() => setRewardPeriod(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* 리워드 요약 카드 */}
        {rewardPerf && (
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-4">
            <SummaryCard
              icon={<Eye className="w-5 h-5 text-blue-500" />}
              label="총 시청"
              value={rewardPerf.summary.totalWatch.toLocaleString()}
              bgColor="bg-blue-50"
            />
            <SummaryCard
              icon={<CheckCircle className="w-5 h-5 text-green-500" />}
              label="완료"
              value={rewardPerf.summary.completed.toLocaleString()}
              bgColor="bg-green-50"
            />
            <SummaryCard
              icon={<XCircle className="w-5 h-5 text-red-500" />}
              label="실패"
              value={rewardPerf.summary.failed.toLocaleString()}
              bgColor="bg-red-50"
            />
            <SummaryCard
              icon={<MinusCircle className="w-5 h-5 text-yellow-500" />}
              label="닫기"
              value={rewardPerf.summary.dismissed.toLocaleString()}
              bgColor="bg-yellow-50"
            />
            <SummaryCard
              icon={<TrendingUp className="w-5 h-5 text-purple-500" />}
              label="완료율"
              value={`${rewardPerf.summary.completionRate}%`}
              bgColor="bg-purple-50"
            />
            <SummaryCard
              icon={<Activity className="w-5 h-5 text-indigo-500" />}
              label="R 시청"
              value={rewardPerf.summary.rewardedWatch.toLocaleString()}
              bgColor="bg-indigo-50"
            />
            <SummaryCard
              icon={<Activity className="w-5 h-5 text-emerald-500" />}
              label="F 시청"
              value={rewardPerf.summary.interstitialWatch.toLocaleString()}
              bgColor="bg-emerald-50"
            />
          </div>
        )}

        {/* 터치포인트별 전환 성과 */}
        {rewardPerf && rewardPerf.touchpoints.length > 0 && (
          <Card>
            <CardHeader>
              <h3 className="text-base font-bold text-gray-900">터치포인트별 전환 성과</h3>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">터치포인트</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">시청</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">완료</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">R 시청/완료</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">F 시청/완료</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">완료율</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rewardPerf.touchpoints.map((tp) => (
                      <tr key={tp.location} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">{tp.label}</td>
                        <td className="px-4 py-3 text-right font-mono">{tp.watchCount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono">{tp.completedCount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono">
                          {tp.rewardedWatchCount.toLocaleString()} / {tp.rewardedCompletedCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {tp.interstitialWatchCount.toLocaleString()} /{' '}
                          {tp.interstitialCompletedCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-medium">{tp.completionRate}%</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-4 py-3">합계</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.watchCount, 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.completedCount, 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.rewardedWatchCount, 0).toLocaleString()} /{' '}
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.rewardedCompletedCount, 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.interstitialWatchCount, 0).toLocaleString()} /{' '}
                        {rewardPerf.touchpoints.reduce((s, t) => s + t.interstitialCompletedCount, 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{rewardPerf.summary.completionRate}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 일별 리워드 추이 차트 */}
        {rewardPerf && rewardPerf.dailyTrend.length > 0 && (
          <Card>
            <CardHeader>
              <h3 className="text-base font-bold text-gray-900">일별 리워드 추이</h3>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={rewardPerf.dailyTrend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="date"
                    stroke="#6B7280"
                    style={{ fontSize: '11px' }}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={40} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="watch" name="시청" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2 }} />
                  <Line
                    type="monotone"
                    dataKey="rewardedWatch"
                    name="R 시청"
                    stroke="#6366F1"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="interstitialWatch"
                    name="F 시청"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    name="완료"
                    stroke="#22C55E"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                  <Line type="monotone" dataKey="failed" name="실패" stroke="#EF4444" strokeWidth={2} dot={{ r: 2 }} />
                  <Line
                    type="monotone"
                    dataKey="dismissed"
                    name="닫기"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 구분선 */}
      <hr className="border-gray-200" />

      {/* ==================== 배너 광고 성과 ==================== */}
      <h3 className="text-base font-bold text-gray-900">배너 광고 성과</h3>

      {/* 요약 카드 4개 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Eye className="w-5 h-5 text-blue-500" />}
          label="PV (노출수)"
          value={totalPv.toLocaleString()}
          bgColor="bg-blue-50"
        />
        <SummaryCard
          icon={<MousePointerClick className="w-5 h-5 text-green-500" />}
          label="Click (클릭수)"
          value={totalClicks.toLocaleString()}
          bgColor="bg-green-50"
        />
        <SummaryCard
          icon={<TrendingUp className="w-5 h-5 text-orange-500" />}
          label="CTR (클릭률)"
          value={`${ctr.toFixed(1)}%`}
          bgColor="bg-orange-50"
        />
        <SummaryCard
          icon={<Users className="w-5 h-5 text-purple-500" />}
          label="활성 광고"
          value={`${activeCount}개`}
          bgColor="bg-purple-50"
        />
      </div>

      {/* 광고별 PV/클릭 차트 */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-bold text-gray-900">광고별 성과 비교</h3>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">등록된 광고가 없습니다.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" style={{ fontSize: '11px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} width={40} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                />
                <Bar dataKey="PV" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="클릭" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* 광고별 상세 테이블 */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-bold text-gray-900">광고별 상세 성과</h3>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">광고명</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">타입</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">PV</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">클릭</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">CTR</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {!statsList || statsList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  statsList.map((item) => (
                    <tr
                      key={item.adId}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedAdId(item.adId);
                        setSelectedAdTitle(item.title);
                      }}
                    >
                      <td className="px-4 py-3 font-medium max-w-[200px] truncate text-blue-600 hover:underline">
                        {item.title}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className={
                            item.adType === 'SURVEY'
                              ? 'border-blue-300 text-blue-600'
                              : 'border-orange-300 text-orange-600'
                          }
                        >
                          {item.adType === 'SURVEY' ? '설문' : '외부'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{item.pv.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{item.clicks.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-medium">{item.ctr.toFixed(1)}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 연계 행동 현황 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              연계 행동 현황
            </h3>
            <div className="flex gap-1">
              {PERIOD_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={actionPeriod === opt.value ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs h-7 px-2"
                  onClick={() => setActionPeriod(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-1">
            {FORMAT_OPTIONS.map((opt) => (
              <Button
                key={opt.label}
                variant={actionFormat === opt.value ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7 px-2"
                onClick={() => setActionFormat(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">행동</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">건수</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {mergedActionStats.map((item) => (
                  <tr key={item.watchLocation} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">{item.watchLocationLabel}</td>
                    <td className="px-4 py-3 text-right font-medium">{item.count.toLocaleString()}건</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold">
                  <td className="px-4 py-3 text-gray-900">합계</td>
                  <td className="px-4 py-3 text-right text-gray-900">{actionTotal.toLocaleString()}건</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/** 요약 카드 컴포넌트 */
const SummaryCard = ({
  icon,
  label,
  value,
  bgColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
}) => (
  <Card className="h-full flex items-center">
    <div className="p-4 w-full flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bgColor}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </Card>
);
